import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IAddPdf, IPdf } from './pdf.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl1-pdf-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IPdf, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class PdfCoreService {
  private _loading = false;

  /**
   * @name pdfList$
   * @description emits stored list of the companies
   */

  pdfList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getPdfs(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`pdf/list`, params || {})
      .subscribe((data: IPdfListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addPdf(data: IAddPdf): Observable<any> {
    this._loading = true;
    return this._api.post('pdf/add', data, {}).pipe(
      tap((pdf: IPdf) => {
        store.update(addEntities(pdf), setProp('lastRefreshed', new Date()));
        this._loading = false;
      }),
    );
  }

  public editPdf(data): Observable<any> {
    this._loading = true;
    return this._api.put('pdf/edit', data, {}).pipe(
      map(() => {
        store.update(
          updateEntities(data['_id'], data),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public deletePdf(data): Observable<any> {
    this._loading = true;
    return this._api.delete('pdf', { body: data }).pipe(
      map(() => {
        store.update(
          deleteEntities(data['_id']),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public getPdfInfo(id: string): IPdf {
    const pdf = store.query(getEntity(id));
    return pdf;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  public resolvePdfId(pdf: string | IPdf): string {
    let pdfId: string | IPdf = '';
    if (pdf) {
      pdfId = pdf;
    }
    if (pdf['_id']) {
      pdfId = pdf['_id'];
    }
    return pdfId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getPdfs();
    }
  }
}

interface IPdfListResponse {
  list: IPdf[];
  totalCount: number;
}
